import { useNavigate } from 'react-router-dom';
import { Icon, icons } from '@/components/common/Icon';
import { SITE_NAME } from '@/config/constants';

const footerLinks = {
  Platform: [
    { title: 'All Integration', href: '/integrations' },
    { title: 'Status', href: 'https://status.gocobalt.io/status/app', external: true },
  ],
  Solutions: [
    { title: 'SaaS', href: '/solutions/saas' },
    { title: 'Finance', href: '/solutions/finance' },
    { title: 'Supply Chain', href: '/solutions/supply-chain' },
    { title: 'Analytics & BI', href: '/solutions/analytics-and-bi' },
  ],
  Resources: [
    { title: 'Case Studies', href: 'https://gocobalt.io/case-studies/', external: true },
    { title: 'Blogs', href: 'https://gocobalt.io/blog', external: true },
    { title: 'Security', href:'/security' },
  ],
  Company: [
    { title: 'About Us', href: '/about-us' },
    { title: 'Careers', href: '/careers' },
    { title: 'Terms', href: '/terms-of-use', external: true },
  ],
  Developer: [
    { title: 'Getting Started', href: 'https://docs.gocobalt.io/quickstart', external: true },
    { title: 'Implementation Guide', href: 'https://docs.gocobalt.io/implementation/overview', external: true },
    { title: 'Github Examples', href: 'https://github.com/gocobalt/cobalt-examples', external: true },
     { title: 'SDKs', href: 'https://docs.gocobalt.io/sdks/overview', external: true },
    { title: 'API references', href: 'https://docs.gocobalt.io/api-reference/overview', external: true },
    { title: 'Deployment', href: 'https://docs.gocobalt.io/deployment/overview', external: true },
  ],
};

const socialLinks = [
  { icon: icons.linkedin, href: 'https://www.linkedin.com/company/gocobalt/', label: 'LinkedIn' },
  { icon: icons.twitter, href: 'https://x.com/gocobaltio', label: 'Twitter' },
  { icon: icons.youtube, href: 'https://www.youtube.com/channel/UCJafcAzUwfu-qq8DXBVasKw', label: 'YouTube' },
];

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Section with Contact Info */}
          <div className="lg:col-span-2">
            <div onClick={() => handleNavigation('/')} className="inline-block cursor-pointer">
              <img src="/black-icon.svg" alt={SITE_NAME} className="h-8" />
            </div>

            <div className="mt-6">
              <p className="text-gray-600">+19253847159</p>
              <p className="text-gray-600">support@gocobalt.io</p>
            </div>

            <div className="mt-6">
              <p className="text-gray-600 font-medium">US Office</p>
              <p className="text-gray-600">16192 Coastal Highway</p>
              <p className="text-gray-600">Lewes, Delaware, 19958</p>
            </div>

            <div className="mt-6">
              <p className="text-gray-600 font-medium">India Office</p>
              <p className="text-gray-600">1st floor, 1502, 19th Main Rd, 1st Sector,</p>
              <p className="text-gray-600">HSR Layout, Bengaluru, Karnataka 560102</p>
            </div>

            {/* Social Media Icons */}
            <div className="mt-6 flex items-center gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900"
                  aria-label={link.label}
                >
                  <Icon icon={link.icon} className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Newsletter Section - Commented Out */}
            {/*
            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Join our newsletter</h3>
              <p className="mt-2 text-sm text-gray-600">
                Get notified about our new components as soon as they are posted
              </p>
              <div className="mt-4 flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button variant="solid" color="primary">
                  Subscribe
                </Button>
              </div>
            </div>
            */}
          </div>

          {/* Links Sections */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:col-span-4 gap-8">
            {/* Platform */}
            <div>
              <h3 className="font-semibold text-gray-900">Platform</h3>
              <ul className="mt-4 space-y-3">
                {footerLinks.Platform.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {link.title}
                      </a>
                    ) : (
                      <span
                        onClick={() => handleNavigation(link.href)}
                        className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer"
                      >
                        {link.title}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="font-semibold text-gray-900">Resources</h3>
              <ul className="mt-4 space-y-3">
                {footerLinks.Resources.map((link) => (
                   <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {link.title}
                      </a>
                    ) : (
                      <span
                        onClick={() => handleNavigation(link.href)}
                        className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer"
                      >
                        {link.title}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold text-gray-900">Company</h3>
              <ul className="mt-4 space-y-3">
                {footerLinks.Company.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {link.title}
                      </a>
                    ) : (
                      <span
                        onClick={() => handleNavigation(link.href)}
                        className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer"
                      >
                        {link.title}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Developer */}
            <div>
              <h3 className="font-semibold text-gray-900">Developer</h3>
              <ul className="mt-4 space-y-3">
                {footerLinks.Developer.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gray-900 text-sm"
                    >
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section with certifications and policies */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Certifications */}
            <div className="flex items-center gap-4">
              <img src="https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+Page-+icons/AICPA.png" alt="SOC 2 Type II Certified" className="h-12" />
              <img src="https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+Page-+icons/ISO.png" alt="ISO 27001" className="h-12" />
              <img src="https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+Page-+icons/GDPR.png" alt="GDPR Compliant" className="h-12" />
            </div>

            {/* Policies */}
            <div className="flex items-center justify-center mx-auto gap-6">
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 cursor-pointer text-sm"
              >
                Privacy Policy
              </a>
              <a
                href="/support-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 cursor-pointer text-sm"
              >
                Support Policy
              </a>
              <span
                onClick={() => {
                  // Dispatch event to show cookie modal
                  const event = new CustomEvent('showCookieModal');
                  document.dispatchEvent(event);
                }}
                className="text-gray-600 hover:text-gray-900 cursor-pointer text-sm"
              >
                Cookie Settings
              </span>
            </div>

            {/* Copyright */}
            <div>
              <span className="text-gray-600 text-sm">
                © {new Date().getFullYear()} {SITE_NAME}. All rights reserved
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;